import { useMutation } from '@tanstack/react-query';
import { useShowErrorToast } from '@utils/formHelpers';
import { queryFetch } from '@services/api';
import { AddAndScheduleTaskReq, AddAndScheduleTaskResp } from './types';
import { useJobRefetch } from '@hooks/job/refetch';
import { defaultAppearance } from '@forms/panel/workflows/task/constants';
import { useToast } from '@hooks/useToast';
import { useTranslation } from 'react-i18next';

export const useAddAndScheduleTask = (callback?: (resp: AddAndScheduleTaskResp) => void) => {
  const { refetch, stages } = useJobRefetch();
  const { showErrorToast } = useShowErrorToast();
  const { toast } = useToast();
  const { t } = useTranslation('app');

  const { mutate, isPending } = useMutation({
    mutationFn: ({
      jobId,
      stageId: _stageId,
      ...task
    }: AddAndScheduleTaskReq): Promise<AddAndScheduleTaskResp> =>
      queryFetch({
        url: `jobs/${jobId}/tasks/add_and_schedule`,
        data: { task },
        opt: { method: 'POST' },
      }),
    onMutate: async ({ jobId, taskName: name, stageId }) =>
      stages.update({
        jobId,
        stage: {
          id: stageId,
          tasks: stages
            .get(jobId)
            ?.find((el) => el.id === stageId)
            ?.tasks.map((el) => ({
              ...el,
              tasks: [...el.tasks, { id: name, name, appearance: defaultAppearance }],
            })),
        },
      }),
    onError: (err, { jobId }, prev) => {
      console.error(err);
      stages.restore(jobId, prev);
    },
    onSuccess: async (resp) => {
      callback?.(resp);
      if (!resp.success) return;
      toast({ title: t('messages.success.task.scheduled') });
      await refetch();
    },
    onSettled: (data, _, { jobId }, prev) => {
      if (!data?.errors) return;
      stages.restore(jobId, prev);
      showErrorToast(data.errors);
    },
  });

  return {
    mutate,
    isLoading: isPending,
  };
};
